import React, { useEffect, useMemo } from 'react';
import { Icon, IconifyIcon } from '@iconify/react';
import { TTag } from './types';
import { TBaseTagContext } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { validate } from '@krugom/validator';
import { selectApiState } from '../../../store/selector/selectApiState';
import { selectIcon } from '../../../store/selector/selectIcon';
import { TSiteIcon } from '../../../types';
import { ApiRequest } from '../../../store/saga/api/actions';
import * as config from '../../../config';
import { find, omit } from 'lodash';
import { icons } from '../../../store/saga/initial/constants/icons';
import { TValidationScheme } from '@krugom/validator/dist/types';

const validationScheme: TValidationScheme = {
  type: 'object',
  properties: {
    icon: { type: 'string', required: true },
  },
};

export const CustomIcon: React.FC<{
  tagProps: TTag;
  context: Partial<TBaseTagContext>;
}> = React.memo(({ tagProps, context }) => {
  const result = validate(tagProps, validationScheme);

  const requestKey = `get-icon-${tagProps.icon}`;

  const stIconSelector = useSelector(
    !result.isValid
      ? () => undefined
      : (state: RootState) => selectIcon(state, tagProps.icon) || icons[tagProps.icon]
  );

  const stIcon = result.isValid ? stIconSelector : undefined;

  const errIconSelector = useSelector(
    (state: RootState) =>
      selectIcon(state, 'error') || find(state.site.icons, { name: 'default-error' })
  );

  const pendIconSelector = useSelector(
    (state: RootState) =>
      selectIcon(state, 'pending') || find(state.site.icons, { name: 'default-pending' })
  );

  const statusSelector = useSelector((state: RootState) => selectApiState(state, requestKey));

  const loadingStatus = result.isValid ? statusSelector?.status || 'init' : 'init';

  const dispatch = useDispatch();

  useEffect(() => {
    if (result.isValid && loadingStatus === 'init' && !stIcon) {
      dispatch(
        ApiRequest({
          path: `${config.api.iconsPath}`,
          method: 'GET',
          key: requestKey,
          query: { filters: { name: tagProps.icon } },
        })
      );
    }
  }, [dispatch, loadingStatus, tagProps.icon, result.isValid, stIcon]);

  const icon =
    stIcon || ((loadingStatus === 'progress' ? pendIconSelector : errIconSelector) as TSiteIcon);

  const iconProps = useMemo(
    () => ({
      ...omit(tagProps, ['children']),
      icon: {
        body: `<path fill="currentcolor" d="${icon.path}" />`,
        width: icon.width,
        height: icon.height,
      } as IconifyIcon,
    }),
    [tagProps, icon]
  );

  if (!icon) return null;

  if (!result.isValid) {
    console.error('Тэг: $Icon;', `Путь: ${context.path?.join('.') || 'Не указан'};`, result.errors);
    return null;
  }

  return <Icon {...iconProps} />;
});
CustomIcon.displayName = 'CustomIcon';
